import React, { Component } from 'react';
import { connect } from 'react-redux';
import propTypes from 'prop-types';
import isEqual from 'lodash.isequal';
import { setTracker } from '~/Redux/Modules/DayTrackers';
import moment from '~/Providers/Moment';
import CustomDragLayer from '~/Components/Global/Stickers/CustomDragLayer';
import PageSplit from '~/Components/Layout/PageSplit/PageSplit';
import Agenda from '~/Containers/Pages/Agenda/Agenda';
import WaterTracking from '~/Containers/Global/Water/WaterTracking';
import MealPrep from '~/Containers/Global/MealPrep/MealPrep';
import Quote from '~/Containers/Global/Quote/Quote';
import CounterColumn from '~/Components/Pages/Calendar/WeekView/TimeGrid/CounterColumn';
import NoScrollBar from '~/Components/Global/NoScrollBar/NoScrollBar';
import ViewHeader from '~/Containers/Pages/Calendar/ViewHeader/ViewHeader';
import Stickers from '~/Containers/Global/Stickers/Stickers';
import StyledEvent from '~/Containers/Pages/Calendar/StyledEvent/StyledEvent';
import { TaskList, CustomTaskList } from '~/Components/Pages/Calendar/Tasks';

import { MUST_DO, OTHER, CUSTOM } from '~/Config/Checklists';
import { DAY_VIEW_OUTSIDE, DAY_VIEW } from '~/Config/CalendarViews';
import { DATE_TIME_FORMAT, DATE_FORMAT } from '~/Redux/Constants';
import StickerDrop from '~/Containers/Global/Stickers/StickerDrop/StickerDrop';
import { overLappingEvents } from '~/Context/Event';
import { DrawingProvider } from '~/Components/Global/Drawing/DrawingProvider';
import TwoPaneResizablePanel, {
  DAY_VIEW_PRIORITY_TASK_HEIGHT,
} from '~/Components/Global/TwoPaneResizablePanel';
import mParticle, { ScreenViewEvent } from '~/Providers/mParticle';
import AppFeatureView, { HABIT_TRACKER } from '~/Components/Global/AppFeatureView';
import Day from '~/Containers/Pages/Calendar/DayView/Day';
import HabitDayTracking from '../../Habits/HabitDayTracking';

import './DayView.scss';

class DayView extends Component {
  constructor() {
    super();

    this.state = {
      allDayEvents: [],
      expanded: false,
    };
  }
  componentDidMount = () => {
    mParticle.logEvent(new ScreenViewEvent('Day View'));
    if (this.props.location.hash) {
      window.location.replace(this.props.location.hash);
    }
    const { match, setDayType } = this.props;
    if (match.params.year && match.params.month && match.params.day) {
      const start = moment().set({
        year: match.params.year,
        month: match.params.month - 1,
        date: match.params.day,
      });

      if (start.isValid()) {
        return setDayType(start);
      }
      return setDayType(moment());
    }
    return setDayType(moment());
  };

  handleMakeAllDayEvent = () => {
    const { openPresetModal, start } = this.props;
    openPresetModal({
      forceUpdate: true,
      isAllDay: true,
      start: start
        .clone()
        .startOf('day')
        .format(DATE_TIME_FORMAT),
      end: start
        .clone()
        .startOf('day')
        .add(1, 'day')
        .format(DATE_TIME_FORMAT),
      color: this.props.defaultCalendar.color,
    });
  };

  componentWillReceiveProps = nextProps => {
    if (
      Object.keys(nextProps.events).length &&
      (!isEqual(nextProps.events, this.props.events) || !nextProps.start.isSame(this.props.start))
    ) {
      this.setState({
        allDayEvents: overLappingEvents(
          nextProps.events,
          moment(nextProps.start).tz(nextProps.userTimeZone),
          moment(nextProps.start)
            .tz(nextProps.userTimeZone)
            .endOf('day'),
          true
        ),
      });
    }
  };

  toggle = (e, change) => {
    e.stopPropagation();
    this.setState({ expanded: change });
  };

  generateMoreToggle = currentEvents => {
    const moreLength = 3;
    if (!this.state.expanded && currentEvents && currentEvents.length > moreLength) {
      return (
        <div className="more-events-wrapper">
          <div onClick={e => this.toggle(e, true)} className="more-events">
            <span className="tablet">+ {currentEvents.length - moreLength} MORE</span>
            <span className="mobile">+ {currentEvents.length - moreLength}</span>
          </div>
        </div>
      );
    }
  };

  generateLessToggle = () => {
    if (this.state.expanded) {
      return <div key="close-more" className="close-more" onClick={e => this.toggle(e, false)} />;
    }
  };

  render() {
    const { start, activeTracker } = this.props;

    return (
      <Agenda>
        <CustomDragLayer />
        <DrawingProvider
          views={[DAY_VIEW_OUTSIDE]}
          startDate={start.format(DATE_FORMAT)}
          endDate={start.format(DATE_FORMAT)}
        >
          <StickerDrop view={DAY_VIEW_OUTSIDE} date={start.format(DATE_FORMAT)}>
            <PageSplit leftClassName="day-left-side" rightClassName="day-right-side">
              <div className="left-side">
                <ViewHeader />
                <Stickers />
                <div className="day-events">
                  <div
                    className={`all-day-events ${this.state.expanded ? 'expanded' : ''}`}
                    onClick={this.handleMakeAllDayEvent}
                    ref={r => (this.sizeRef = r)}
                  >
                    {this.generateLessToggle()}
                    {(this.state.expanded
                      ? this.state.allDayEvents
                      : this.state.allDayEvents.slice(0, 3)
                    ).map((allDayEvent, index) => {
                      return <StyledEvent key={index} event={allDayEvent} />;
                    })}
                    {this.generateMoreToggle(this.state.allDayEvents)}
                  </div>
                  <div className="events-wrapper">
                    <NoScrollBar>
                      <DrawingProvider
                        views={[DAY_VIEW]}
                        startDate={start.format(DATE_FORMAT)}
                        endDate={start.format(DATE_FORMAT)}
                      >
                        <div className="flex-row">
                          <div className="flex-1 counter">
                            <CounterColumn showMeridian />
                          </div>
                          <Day stickerView={DAY_VIEW} day={start} />
                        </div>
                      </DrawingProvider>
                    </NoScrollBar>
                  </div>
                </div>
              </div>
              <div className="right-side">
                <div className="right-side-upper">
                  <TwoPaneResizablePanel
                    className="right-side-upper-main"
                    firstChild={
                      <div className="must-do-today">
                        <TaskList
                          date={start}
                          type={MUST_DO}
                          header="PRIORITY TASKS"
                          editableHeader
                        />
                      </div>
                    }
                    secondChild={
                      <div className="other-tasks">
                        <TaskList date={start} type={OTHER} header="OTHER TASKS" editableHeader />
                      </div>
                    }
                    firstChildInitialSize={134}
                    minimumSize={30}
                    persistenceKey={DAY_VIEW_PRIORITY_TASK_HEIGHT}
                  />
                  <div className="right-side-upper-sidebar">
                    <Quote showHeader={false} showFooter={false} />
                    <MealPrep editableHeader />
                    <AppFeatureView featureName={HABIT_TRACKER} disabledView={<WaterTracking />}>
                      {activeTracker === 'habit' ? <HabitDayTracking /> : <WaterTracking />}
                    </AppFeatureView>
                  </div>
                </div>
                <div className="right-side-lower">
                  <div className="my-custom-list">
                    <CustomTaskList date={start} type={CUSTOM} />
                  </div>
                </div>
              </div>
            </PageSplit>
          </StickerDrop>
        </DrawingProvider>
      </Agenda>
    );
  }
}

DayView.propTypes = {
  location: propTypes.object,
  match: propTypes.object,
  setDayType: propTypes.func,
  events: propTypes.object,
  start: propTypes.object,
  end: propTypes.object,
  openPresetModal: propTypes.func,
  defaultCalendar: propTypes.object,
  userTimeZone: propTypes.string,
  setTracker: propTypes.func,
  activeTracker: propTypes.string,
};

const mapStateToProps = state => ({
  activeTracker: state.DayTrackers.activeTracker,
});

const mapDispatchToProps = dispatch => ({
  setTracker: opt => dispatch(setTracker(opt)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DayView);
